import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  FaCog,
  FaListAlt,
  FaMobileAlt,
  FaCheckCircle,
  FaUsers,
  FaChevronRight,
  FaPuzzlePiece,
} from 'react-icons/fa';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 2rem;
  background-color: var(--color-bg-secondary);
  min-height: 100vh;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Header = styled.header`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const TitleIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 12px;
  background: var(--color-btn-primary-bg);
  color: var(--color-btn-primary-text);
  border: 1px solid var(--color-btn-primary-border);
  font-size: 1.1rem;
  box-sizing: border-box;
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 650;
  letter-spacing: -0.02em;
  line-height: 1.2;
  color: var(--color-text-primary);
`;

const Subtitle = styled.p`
  margin: 0;
  color: var(--color-text-secondary);
  font-size: 0.95rem;
  line-height: 1.5;
  max-width: 520px;
`;

const TilesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Tile = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1.25rem;
  background: var(--color-bg-primary);
  border: 1px solid var(--color-border-primary);
  border-radius: 16px;
  cursor: pointer;
  text-align: left;
  transition: all 0.2s ease;
  box-shadow: var(--shadow-sm);
  min-height: 148px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
    border-color: ${({ $accent }) => `${$accent}55`};
  }

  &:active {
    transform: translateY(0);
  }
`;

const TileTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
  gap: 0.75rem;
`;

const TileIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 12px;
  background: ${({ $accent }) => `${$accent}18`};
  color: ${({ $accent }) => $accent};
  font-size: 1.1rem;
  flex-shrink: 0;
`;

const TileArrow = styled.span`
  color: var(--color-secondary);
  font-size: 0.85rem;
  margin-top: 0.35rem;
  transition: transform 0.2s, color 0.2s;

  ${Tile}:hover & {
    color: ${({ $accent }) => $accent};
    transform: translateX(2px);
  }
`;

const TileTitle = styled.h2`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--color-text-primary);
`;

const TileDescription = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: var(--color-text-secondary);
  line-height: 1.45;
`;

const SETTINGS_SECTIONS = [
  {
    key: 'ticket-statuses',
    title: 'Status de tickets',
    description: 'Configure status, categorias e tipos de demanda aplicáveis.',
    to: '/admin/ticket-statuses',
    icon: FaListAlt,
    accent: '#2563EB',
  },
  {
    key: 'demand-types',
    title: 'Tipos de demanda',
    description: 'Gerencie os tipos de demanda disponíveis nos tickets e filtros.',
    to: '/admin/demand-types',
    icon: FaListAlt,
    accent: '#0891B2',
  },
  {
    key: 'app-origins',
    title: 'Aplicações (origem)',
    description: 'Gerencie aplicações, cores e identificadores de origem.',
    to: '/admin/app-origins',
    icon: FaMobileAlt,
    accent: '#7C3AED',
  },
  {
    key: 'widgets',
    title: 'Widgets',
    description: 'Configure o widget embutido e o preview para apps Elevate.',
    to: '/admin/widgets',
    icon: FaPuzzlePiece,
    accent: '#0F766E',
  },
  {
    key: 'resolution-types',
    title: 'Tipos de resolução',
    description: 'Defina os motivos ao finalizar tickets de suporte e bug.',
    to: '/admin/resolution-types',
    icon: FaCheckCircle,
    accent: '#059669',
  },
  {
    key: 'users',
    title: 'Usuários',
    description: 'Habilite responsáveis atribuíveis e gerencie contas.',
    to: '/admin/users',
    icon: FaUsers,
    accent: '#DB2777',
  },
];

const SettingsHubPage = () => {
  const navigate = useNavigate();

  return (
    <PageContainer>
      <Header>
        <TitleRow>
          <TitleIcon aria-hidden>
            <FaCog />
          </TitleIcon>
          <PageTitle>Configurações</PageTitle>
        </TitleRow>
        <Subtitle>
          Escolha uma área para configurar o sistema. Cada seção abre em uma tela dedicada.
        </Subtitle>
      </Header>

      <TilesGrid>
        {SETTINGS_SECTIONS.map((section) => {
          const Icon = section.icon;
          return (
            <Tile
              key={section.key}
              type="button"
              $accent={section.accent}
              onClick={() => navigate(section.to)}
            >
              <TileTop>
                <TileIcon $accent={section.accent} aria-hidden>
                  <Icon />
                </TileIcon>
                <TileArrow $accent={section.accent} aria-hidden>
                  <FaChevronRight />
                </TileArrow>
              </TileTop>
              <TileTitle>{section.title}</TileTitle>
              <TileDescription>{section.description}</TileDescription>
            </Tile>
          );
        })}
      </TilesGrid>
    </PageContainer>
  );
};

export default SettingsHubPage;
